import { useCallback } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import { useSelector } from 'react-redux';
import { OrdersExportResponseDto } from '@tiendanube/common';
import { useAppDispatch } from 'App/store';
import { useListFilters } from 'commons/hooks';
import {
  dateFormat,
  Format,
  getTodayIsoString,
  ninetyDaysAgo,
  subtractDays,
} from 'commons/utils/date';
import {
  ExportFiltersType,
  FilterStatusEnum,
  fulfillmentStatusFilter,
  paymentStatusFilter,
  statusFilter,
} from 'domains/Orders/Orders/ordersService';
import {
  fetchTotalOrdersExport,
  exportOrders as exportOrdersAction,
} from 'domains/Orders/Orders/ordersSlice';
import {
  getOrdersExportStatus,
  getOrdersExportTotal,
} from 'domains/Orders/Orders/ordersSlice/ordersSelectors';

interface UseOrdersExportResult {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  filters: ExportFiltersType;
  totalResults: number;
  getTotalOrders: (newFilters: ExportFiltersType) => Promise<void>;
  refreshTotalOrders: () => Promise<void>;
  removeFilter: (id: string) => Promise<void>;
  exportOrders: () => Promise<OrdersExportResponseDto>;
  clearFilters: () => Promise<void>;
}

export const defaultFilters: ExportFiltersType = {
  q: '',
  dateFrom: subtractDays(getTodayIsoString(), 30),
  dateTo: getTodayIsoString(),
  status: FilterStatusEnum.EMPTY,
  paymentStatus: FilterStatusEnum.EMPTY,
  fulfillmentStatus: FilterStatusEnum.EMPTY,
  paymentMethods: '',
  paymentProvider: '',
  shippingMethod: '',
  origin: '',
  appId: '',
  products: '',
  exactProductSet: '',
  minUnits: '',
  maxUnits: '',
  isWholesale: '',
  couponIdsRaw: '',
  stockIssues: '',
};

function useOrdersExport(): UseOrdersExportResult {
  const dispatch = useAppDispatch();
  const { isLoading, isError, isSuccess } = useSelector(getOrdersExportStatus);
  const { filters, changeFilters } = useListFilters(
    'orders/export',
    defaultFilters,
  );
  const totalResults = useSelector(getOrdersExportTotal);

  const getTotalOrders = useCallback(
    async (newFilters: ExportFiltersType) => {
      const filtersToApply = newFilters;
      if (!isEqual(filters, filtersToApply)) {
        changeFilters(filtersToApply);
        await dispatch(fetchTotalOrdersExport(newFilters));
      }
    },
    [changeFilters, dispatch, filters],
  );

  const refreshTotalOrders = useCallback(async () => {
    await dispatch(fetchTotalOrdersExport(filters));
  }, [dispatch, filters]);

  const removeFilter = useCallback(
    async (id: string) => {
      const hasFiltersWithDateRestrictionsApplied =
        filters.paymentMethods !== '' ||
        filters.paymentProvider !== '' ||
        filters.appId !== '';

      const dateAndPaymentsFilter =
        hasFiltersWithDateRestrictionsApplied && id === 'dateFrom';
      const dateAfterNinetyDays = filters.dateFrom > ninetyDaysAgo;

      if (dateAndPaymentsFilter && !dateAfterNinetyDays) return;

      const hasStatusesFilters =
        id.startsWith(`${paymentStatusFilter}-`) ||
        id.startsWith(`${fulfillmentStatusFilter}-`);

      const hasProductsFilter = id.startsWith('variant-');
      const hasCouponsFilter = id.startsWith('couponIdsRaw-');
      const isStatusFilter = id === statusFilter;

      let newId = hasStatusesFilters ? id.split('-')[0] : id;
      newId = hasProductsFilter ? 'products' : newId;
      newId = hasCouponsFilter ? 'couponIdsRaw' : newId;

      let newValue =
        dateAndPaymentsFilter && dateAfterNinetyDays ? ninetyDaysAgo : '';

      // When removing status filter, set it to empty (which backend treats as open)
      if (isStatusFilter) {
        newValue = '';
      }

      newValue = hasStatusesFilters
        ? filters[newId]
            .split(',')
            .filter((status) => status !== id.split('-')[1])
            .join(',')
        : newValue;

      newValue = hasProductsFilter
        ? filters.products
            .split(';')
            .filter((variantId) => variantId !== id.split('-')[1])
            .join(';')
        : newValue;

      newValue = hasCouponsFilter
        ? (filters.couponIdsRaw ?? '')
            .split(',')
            .filter((couponId) => couponId !== id.split('-')[1])
            .join(',')
        : newValue;

      const newFilters = { ...filters, [newId]: newValue };
      changeFilters(newFilters);
      await getTotalOrders(newFilters);
    },
    [changeFilters, filters, getTotalOrders],
  );

  const clearFilters = useCallback(async () => {
    const defaultFiltersWithoutDates = {
      ...defaultFilters,
      dateTo: '',
      dateFrom: '',
    };
    changeFilters(defaultFiltersWithoutDates);
    await getTotalOrders(defaultFiltersWithoutDates);
  }, [changeFilters, getTotalOrders]);

  const exportOrders = useCallback(async () => {
    const dateFrom = filters.dateFrom;
    const dateTo = filters.dateTo;
    const formattedDateFrom =
      dateFrom === '' ? dateFrom : dateFormat(dateFrom, Format.DD_MM_YYYY);
    const formattedDateTo =
      dateTo === '' ? dateTo : dateFormat(dateTo, Format.DD_MM_YYYY);
    const result = await dispatch(
      exportOrdersAction({
        ...filters,
        dateFrom: formattedDateFrom,
        dateTo: formattedDateTo,
      }),
    );
    return unwrapResult(result);
  }, [dispatch, filters]);

  return {
    isLoading,
    isSuccess,
    isError,
    filters,
    totalResults,
    refreshTotalOrders,
    getTotalOrders,
    removeFilter,
    exportOrders,
    clearFilters,
  };
}

export default useOrdersExport;
